/**
 * Integration TanStack Query hooks.
 */

import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { integrationKeys } from "@/queries/keys";
import * as integrationApi from "./api";
import { isActiveCalendar, type CalendarType } from "@speakai/common/src/calendar/api";
import type { CalendarEventsFilter } from "./api";

/** Fetch all connected integration accounts for the hub page. */
export function useIntegrationAccounts(options: { enabled?: boolean } = {}) {
  return useQuery({
    enabled: options.enabled ?? true,
    queryKey: integrationKeys.accounts(),
    queryFn: async () => {
      const res = await integrationApi.getIntegrations();
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch integrations");
      return res.data!;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useIntegrationsCatalog() {
  return useQuery({
    queryKey: integrationKeys.catalog(),
    queryFn: async () => {
      const res = await integrationApi.getIntegrationsCatalog();
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch integrations catalog");
      return Array.isArray(res.data) ? res.data : [];
    },
    staleTime: 5 * 60 * 1000,
  });
}

// -- Calendar -------------------------------------------------------

/**
 * Whether calendar integrations are disabled for this company (admin-controlled).
 * Server response shape: `{ showBanner: boolean; calendarType?: string }`.
 */
export function useIsCalendarDisabled(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: integrationKeys.calendarDisabled(),
    queryFn: async () => {
      const res = await integrationApi.isCalendarDisabled();
      if (!res.success)
        throw new Error(res.error ?? "Failed to check calendar disabled flag");
      return Boolean(res.data?.showBanner);
    },
    staleTime: 5 * 60 * 1000,
    enabled: options.enabled ?? true,
  });
}

/** Fetch calendar account details for a specific provider. */
export function useCalendarAccount(type: CalendarType) {
  return useQuery({
    queryKey: integrationKeys.calendar(type),
    queryFn: async () => {
      const res = await integrationApi.getCalendarAccountDetails(type);
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch calendar account");
      const data = res.data;
      if (data && data.length > 0) {
        return data[0].calendars;
      }
      return null;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** Fetch calendar events with filtering and pagination. */
export function useCalendarEvents(
  type: CalendarType,
  page: number,
  pageSize: number,
  eventsFilter: CalendarEventsFilter,
  enabled = true,
) {
  return useQuery({
    queryKey: integrationKeys.calendarEvents(type, {
      page,
      pageSize,
      ...eventsFilter,
    }),
    queryFn: async () => {
      const res = await integrationApi.getCalendarEvents(
        type,
        page,
        pageSize,
        eventsFilter,
      );
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch calendar events");
      return res.data!;
    },
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
  });
}

// -- Slack ----------------------------------------------------------

/** Fetch Slack account info. */
export function useSlackAccounts() {
  return useQuery({
    queryKey: integrationKeys.slack(),
    queryFn: async () => {
      const res = await integrationApi.getSlackAccounts();
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch Slack accounts");
      return Array.isArray(res.data) ? res.data : [];
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** Fetch Slack channels for a connected account. */
export function useSlackChannels(accountId: string | undefined) {
  return useQuery({
    queryKey: integrationKeys.slackChannels(accountId!),
    queryFn: async () => {
      const res = await integrationApi.getSlackChannels(accountId!);
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch Slack channels");
      return Array.isArray(res.data) ? res.data : [];
    },
    enabled: !!accountId,
    staleTime: 5 * 60 * 1000,
  });
}

// -- Vimeo ----------------------------------------------------------

/** Fetch Vimeo account details. */
export function useVimeoAccount() {
  return useQuery({
    queryKey: integrationKeys.vimeo(),
    queryFn: async () => {
      const res = await integrationApi.getVimeoDetails();
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch Vimeo account");
      const data = res.data;
      if (data && data.length > 0) {
        return data[0];
      }
      return null;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** Fetch Vimeo videos with pagination. */
export function useVimeoVideos(page: number, pageSize: number, enabled = true) {
  return useQuery({
    queryKey: integrationKeys.vimeoVideos({ page, pageSize }),
    queryFn: async () => {
      const res = await integrationApi.getVimeoVideos(page, pageSize);
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch Vimeo videos");
      return res.data!;
    },
    enabled,
    placeholderData: keepPreviousData,
  });
}

/** Fetch Vimeo folders with pagination. */
export function useVimeoFolders(
  page: number,
  pageSize: number,
  enabled = true,
) {
  return useQuery({
    queryKey: integrationKeys.vimeoFolders({ page, pageSize }),
    queryFn: async () => {
      const res = await integrationApi.getVimeoFolders(page, pageSize);
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch Vimeo folders");
      return res.data!;
    },
    enabled,
    placeholderData: keepPreviousData,
  });
}

/** Fetch Vimeo albums with pagination. */
export function useVimeoAlbums(page: number, pageSize: number, enabled = true) {
  return useQuery({
    queryKey: integrationKeys.vimeoAlbums({ page, pageSize }),
    queryFn: async () => {
      const res = await integrationApi.getVimeoAlbums(page, pageSize);
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch Vimeo albums");
      return res.data!;
    },
    enabled,
    placeholderData: keepPreviousData,
  });
}

/** Fetch videos within a Vimeo folder. */
export function useVimeoFolderVideos(
  folderId: string,
  page: number,
  pageSize: number,
  enabled = true,
) {
  return useQuery({
    queryKey: integrationKeys.vimeoFolderVideos(folderId, { page, pageSize }),
    queryFn: async () => {
      const res = await integrationApi.getVimeoFolderVideos(
        folderId,
        page,
        pageSize,
      );
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch folder videos");
      return res.data!;
    },
    enabled: !!folderId && enabled,
    placeholderData: keepPreviousData,
  });
}

/** Fetch videos within a Vimeo album. */
export function useVimeoAlbumVideos(
  albumId: string,
  page: number,
  pageSize: number,
  enabled = true,
) {
  return useQuery({
    queryKey: integrationKeys.vimeoAlbumVideos(albumId, { page, pageSize }),
    queryFn: async () => {
      const res = await integrationApi.getVimeoAlbumVideos(
        albumId,
        page,
        pageSize,
      );
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch album videos");
      return res.data!;
    },
    enabled: !!albumId && enabled,
    placeholderData: keepPreviousData,
  });
}

// -- Calendar Accounts (all) ----------------------------------------

/** Fetch all connected calendar accounts (Google + Outlook). Unwraps the `.calendars` wrapper from the API response. */
export function useCalendarAccounts() {
  return useQuery({
    queryKey: integrationKeys.calendarAccounts(),
    queryFn: async () => {
      const res = await integrationApi.getAllCalendarAccounts();
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch calendar accounts");
      // API returns { calendars: CalendarAccount }[] — unwrap to CalendarAccount[]
      return (res.data ?? []).map((item) => item.calendars);
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** Fetch only active connected calendar accounts (Google + Outlook). */
export function useActiveCalendarAccounts(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [...integrationKeys.calendarAccounts(), "active-only"],
    queryFn: async () => {
      const res = await integrationApi.getAllCalendarAccounts();
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch calendar accounts");
      return (res.data ?? []).map((item) => item.calendars).filter(isActiveCalendar);
    },
    staleTime: 5 * 60 * 1000,
    enabled: options?.enabled ?? true,
  });
}

/** Fetch unique calendar event titles for autocomplete. */
export function useUniqueCalendarEvents() {
  return useQuery({
    queryKey: integrationKeys.uniqueCalendarEvents(),
    queryFn: async () => {
      const res = await integrationApi.getUniqueCalendarEvents();
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch unique calendar events");
      return (res.data ?? []) as string[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

// -- Google Drive ---------------------------------------------------

/** Fetch Google Drive account details. */
export function useGoogleDriveAccount() {
  return useQuery({
    queryKey: integrationKeys.googleDrive(),
    queryFn: async () => {
      const res = await integrationApi.getGoogleDriveAccounts();
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch Google Drive account");
      const data = res.data;
      if (data && data.length > 0) {
        return data[0];
      }
      return null;
    },
    staleTime: 5 * 60 * 1000,
  });
}
